import axios from "axios";
import { isNil, uniq } from "lodash-es";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { DetailedAlert } from "../components/DetailedAlert";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";
import { StyledSelect as Select } from "../components/inputs/StyledSelect";
import { LoadingButton } from "../components/LoadingButton";
import { LoadingIcon } from "../components/LoadingIcon";
import { Toolbar } from "../components/Toolbar";
import { createStartDate } from "../dashboard";
import { api, extractErrorDetails, extractErrorMessage } from "../util/api";
import { isBlank } from "../util/helpers";
import { encodeTableState } from "../util/table";
import { AssignableCasesTable } from "./assignment/AssignableCasesTable";
import { AssigneeStats } from "./assignment/AssigneeStats";
import { AssignmentSearchForm, useAssigneeCounts } from "./AssignmentSearch";

const START_DATE = createStartDate({ days: 90 });

const ReassignmentToolbar = ({ account, assignableUsers, selectedCaseIds, handleResponse }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [assignee, setAssignee] = useState(null);

  const assignableUserOptions = useMemo(() => {
    return [
      {
        value: null,
        label: <span className="fw-bolder">Unassign</span>,
      },
    ].concat(
      assignableUsers
        .filter((au) => au.email !== account?.email)
        .map(({ email, name }) => ({ value: email, label: name || email }))
        .sort((userA, userB) => (userA.label.toLowerCase() < userB.label.toLowerCase() ? -1 : 1))
    );
  }, [assignableUsers, account]);

  const reassignButtonDisabled = selectedCaseIds.length == 0 || assignee == null;

  const handleReassign = async () => {
    setLoading(true);
    setResult(null);

    try {
      const result = await api.post("/cases/assign_user", { case_ids: selectedCaseIds, assignee: assignee.value });

      setResult({
        message: result.data.meta.message,
        details: result.data.meta.error_details,
        messageStyle: (result.data.meta.error_details ?? []).length > 0 ? "warning" : "success",
      });

      handleResponse(result);
    } catch (err) {
      setResult({
        message: extractErrorMessage(err),
        details: extractErrorDetails(err),
        messageStyle: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const reassignButtonText =
    selectedCaseIds.length > 0 ? (
      <>
        Reassign <strong>{selectedCaseIds.length}</strong> Cases
      </>
    ) : (
      "Reassign"
    );

  return (
    <>
      {result && <DetailedAlert {...result} />}
      <Toolbar>
        <div className="ml-auto">
          <span className="me-2 d-inline-block" style={{ minWidth: 320 }}>
            <Select
              aria-label="Reassign to"
              type="select"
              isClearable
              isSearchable
              autoFocus
              value={assignee}
              onChange={setAssignee}
              options={assignableUserOptions}
              placeholder="Select Assignee"
            />
          </span>

          <LoadingButton
            onClick={handleReassign}
            loading={loading}
            disabled={reassignButtonDisabled || loading}
            color="primary"
            style={{ gap: 2 }}
          >
            {reassignButtonText}
          </LoadingButton>
        </div>
      </Toolbar>
    </>
  );
};

const serializeFilters = ({ labIds, payorIds, clinicIds, status, createdDate, taskDueDate, onshore, payorPaths }, accountEmail) => {
  const filters = [
    {
      id: "lab",
      value: labIds,
    },
    {
      id: "payor",
      value: payorIds,
    },
    {
      id: "clinic",
      value: clinicIds,
    },
    {
      id: "status",
      value: status,
    },
    {
      id: "created_at",
      value: {
        operation: "before",
        args: createdDate,
      },
    },
    {
      id: "due_date",
      value: {
        operation: "equals",
        args: taskDueDate,
      },
    },
    {
      id: "onshore",
      value: onshore,
    },
    {
      id: "payor_paths",
      value: payorPaths,
    },
    {
      id: "assignee",
      value: accountEmail,
    },
  ];
  return { filtered: filters?.filter((f) => !isBlank(f?.value)) };
};

export const ReassignmentSearch = ({ accountId, assignableUsers, searchableStatuses, payorPathDefaults, showUserNotes }) => {
  const {
    loading: accountLoading,
    error: accountError,
    value: account,
    fetch: fetchAccount,
    everLoaded: accountEverLoaded,
  } = useAssigneeCounts(accountId);

  const [assignedCases, setAssignedCases] = useState([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);

  const tableRef = useRef();
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(null);
  const [pageLength, setPageLength] = useState(0);
  const [rows, setRows] = useState(assignedCases);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (rawTableState) => {
      const filterParam = filters ? serializeFilters(filters, account.email) : { filtered: [{ id: "assignee", value: account.email }] };

      // show the loading overlay
      setLoading(true);

      try {
        const dataReq = await api.get("/cases.json", {
          params: {
            ...encodeTableState(rawTableState),
            ...filterParam, // Filters from the form
            // always set searches
            case_type: "type_prior_auth",
            hide_done: true,
            hide_assigned: false,
            hide_held: true,
            start_date: START_DATE,
            sort_for_assign_and_reassign: true,
          },
        });

        setRows(dataReq.data.rows);
        setPages(dataReq.data.pages);
        setPageLength(dataReq.data.rows.length);
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error(e);
        }
      } finally {
        setLoading(false);
      }
    },
    [account?.email, filters]
  );

  const handleResponse = useCallback(
    async (response) => {
      const failedCaseIds = response.data?.updated?.invalid_assignments?.map?.((ia) => ia.id) ?? [];
      const reassignedCaseIds = selectedCaseIds.filter((id) => !failedCaseIds.includes(id));

      setAssignedCases((prev) => prev.filter((c) => !reassignedCaseIds.includes(c.public_id)));
      setSelectedCaseIds((prev) => prev.filter((id) => failedCaseIds.includes(id)));

      await Promise.all([tableRef?.current?.updateTableData(), fetchAccount()]);
    },
    [selectedCaseIds, fetchAccount]
  );

  const handleSearch = useCallback((filters) => {
    setFilters(() => {
      return filters;
    });

    tableRef.current.resetPage();
  }, []);

  if (accountLoading && !accountEverLoaded) {
    return <LoadingIcon />;
  }

  return (
    <>
      <Card className="mb-3">
        {showUserNotes && account && (
          <Row className="px-4 pt-4">
            <Col>
              <h6>User Notes:</h6>
              <blockquote className="text-muted">{account.notes ?? "No notes..."}</blockquote>
            </Col>
          </Row>
        )}
        <AssigneeStats account={account} />
      </Card>

      {!accountLoading && !account && <DetailedErrorAlert message={`Could not find account for id ${accountId}`} />}
      {accountError && <DetailedErrorAlert error={accountError} />}

      <Card className="mb-3">
        <AssignmentSearchForm
          onFormSubmit={handleSearch}
          account={account}
          searchableStatuses={searchableStatuses}
          isOffshoreAccount={isNil(account?.is_onshore) ? undefined : !account.is_onshore}
          payorPathDefaults={payorPathDefaults}
          noPreload
          noDefault
        />
      </Card>

      <Card>
        <CardHeader>
          <ReassignmentToolbar
            account={account}
            selectedCaseIds={selectedCaseIds}
            assignableUsers={assignableUsers}
            handleResponse={handleResponse}
          />
        </CardHeader>

        <CardBody className="p-0">
          <AssignableCasesTable
            testId="reassignable-cases-grid"
            ref={tableRef}
            data={rows}
            loading={loading}
            onFetchData={fetchData}
            onSelectionChange={(selection) => setSelectedCaseIds(uniq(selection))}
            page={page}
            pages={pages}
            showPagination={true}
            pageSize={pageLength}
            defaultPageSize={assignedCases?.length > 0 ? assignedCases?.length : 10}
            onPageSizeChange={(size) => setPageLength(size)}
          />
        </CardBody>
      </Card>
    </>
  );
};
